import "./global.css";
import "./theme/theme.css";
import "./locales/i18n";

import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, RouterProvider, createHashRouter } from "react-router";
import App from "./App";
import { registerLocalIcons } from "./components/icon";
import PageError from "./pages/sys/error/PageError";
import { routesSection } from "./routes/sections";
import { observeAllVitals, generatePerformanceReport } from "./utils/performance";
import { ThemeProvider } from "@/contexts/ThemeContext";

await registerLocalIcons();

// 全局禁用输入框自动完成功能
if (typeof window !== 'undefined') {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	// 禁用输入框自动完成（只处理新添加的元素）
	const disableAutocomplete = (elements: NodeList | Element[]) => {
		const inputs = Array.from(elements).flatMap(el =>
			el instanceof Element
				? Array.from(el.querySelectorAll('input, textarea'))
				: []
		);

		inputs.forEach((input) => {
			if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
				// 只处理还没有设置过的元素
				if (!input.hasAttribute('data-autocomplete-disabled')) {
					input.setAttribute('autocomplete', 'off');
					input.setAttribute('autocorrect', 'off');
					input.setAttribute('autocapitalize', 'off');
					input.setAttribute('spellcheck', 'false');
					input.setAttribute('data-autocomplete-disabled', 'true');
				}
			}
		});
	};

	// 防抖处理的回调
	const debouncedDisableAutocomplete = (mutations: MutationRecord[]) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			// 只处理新添加的节点
			const addedNodes: Node[] = [];
			mutations.forEach(mutation => {
				mutation.addedNodes.forEach(node => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						addedNodes.push(node);
					}
				});
			});

			if (addedNodes.length > 0) {
				disableAutocomplete(addedNodes as Element[]);
			}
			timeoutId = null;
		}, 150); // 150ms 防抖
	};

	// 初始禁用现有输入框
	disableAutocomplete([document.body]);

	// 使用更高效的 MutationObserver
	const observer = new MutationObserver(debouncedDisableAutocomplete);

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// 在页面卸载时断开 observer，防止内存泄漏
	window.addEventListener('beforeunload', () => {
		if (timeoutId) clearTimeout(timeoutId);
		observer.disconnect();
	});
}

// 性能监控：监控所有 Web Vitals
if (typeof window !== 'undefined') {
	observeAllVitals((metric) => {
		// 开发环境输出到控制台
		if (import.meta.env.DEV) {
			console.log(`📊 ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`);
		}

		// 生产环境可以发送到分析服务
		// 例如: Google Analytics, Vercel Analytics 等
		if (typeof (window as any).gtag !== 'undefined') {
			(window as any).gtag('event', metric.name, {
				value: Math.round(metric.value),
				metric_rating: metric.rating,
				event_category: 'Web Vitals',
			});
		}
	});

	// 页面卸载时生成性能报告
	window.addEventListener('beforeunload', () => {
		if (import.meta.env.DEV) {
			generatePerformanceReport();
		}
	});
}

const router = createHashRouter([
	{
		Component: () => (
			<App>
				<Outlet />
			</App>
		),
		errorElement: <ErrorBoundary fallbackRender={PageError} />,
		children: routesSection,
	},
]);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
	<ThemeProvider defaultTheme="system">
		<RouterProvider router={router} />
	</ThemeProvider>
);
